import { Observable, of } from 'rxjs'

/**
 * Two synchronous DOM-walk mechanisms, not one window-broadcast — they serve
 * genuinely different questions and are never interchangeable:
 *
 *   - `discoverComponent`/`discoverAnyComponent` — "what's the nearest
 *     ANCESTOR of this element with tag X?" A composed-tree walk upward from
 *     an explicit `origin`, crossing shadow boundaries via `assignedSlot` /
 *     `parentElement` / the enclosing shadow root's host — the same climb
 *     `overlay.service.ts`'s `findOverlayBoundary` already proves correct.
 *
 *   - `discoverElement`/`discoverAllElements`/`discover` — "find element(s)
 *     matching a selector ANYWHERE under this root, piercing shadow DOM?" A
 *     recursive descendant walk, default root `document`. `querySelector`
 *     already matches a bare tag name as well as `#id`/`.class`/`[attr]`, so
 *     `discover()` needs no CSS-selector-vs-tag-name branch — every query
 *     shape routes through the same walker.
 *
 * `discoverAscendingPeer` composes both of the above for a third question
 * neither answers alone — "what's the nearest peer of my OWN kind, whether
 * it wraps me or sits beside my own content?" — see its own doc.
 *
 * All three resolve synchronously — there is nothing to wait for, so there
 * is no timeout parameter. A caller that genuinely needs to wait for
 * something not yet mounted (e.g. a just-inserted subtree whose first Lit
 * render is still a microtask away) owns that wait itself, at the call site
 * — see `pathway.ts`'s single `requestAnimationFrame` gate before its first
 * discovery pass.
 */

/** One step up the composed tree — shared by every ancestor-climbing search below. */
function stepUp(node: Element): Element | null {
	const root = node.getRootNode()
	return node.assignedSlot ?? node.parentElement ?? (root instanceof ShadowRoot ? root.host : null)
}

/**
 * The nearest of `origin` and its ancestors in the flattened tree that
 * `predicate` accepts: a slotted node climbs through the slot it renders in,
 * the same path a composed event takes.
 */
export function climbToAncestor(origin: Element | null, predicate: (el: Element) => boolean): Element | null {
	let node: Element | null = origin
	while (node) {
		if (predicate(node)) return node
		node = stepUp(node)
	}
	return null
}

/**
 * Recursive shadow-piercing descendant walk. `root`'s own shadow root first
 * (when `root` is itself a shadow host — a wrapper component whose real
 * content lives one shadow boundary in, e.g. `<nav-rail>` around
 * `<schmancy-navigation-rail>`), then `root`'s light DOM, then each open
 * shadow root that light DOM contains, in encounter order.
 */
function walkShadowPiercing(root: ParentNode, selector: string): Element | null {
	if (root instanceof Element && root.shadowRoot) {
		const ownShadow = walkShadowPiercing(root.shadowRoot, selector)
		if (ownShadow) return ownShadow
	}
	const direct = root.querySelector(selector)
	if (direct) return direct
	for (const el of root.querySelectorAll('*')) {
		if (el.shadowRoot) {
			const found = walkShadowPiercing(el.shadowRoot, selector)
			if (found) return found
		}
	}
	return null
}

function walkShadowPiercingAll(root: ParentNode, selector: string, results: Element[] = []): Element[] {
	if (root instanceof Element && root.shadowRoot) {
		walkShadowPiercingAll(root.shadowRoot, selector, results)
	}
	results.push(...root.querySelectorAll(selector))
	for (const el of root.querySelectorAll('*')) {
		if (el.shadowRoot) walkShadowPiercingAll(el.shadowRoot, selector, results)
	}
	return results
}

/**
 * The nearest ancestor of `origin` whose tag name is `componentTag` —
 * self-inclusive, so `origin` itself counts (matches `findOverlayBoundary`'s
 * convention). `null` when `origin` is `null` (nothing to climb from) or no
 * ancestor matches.
 *
 * @example discoverComponent<HTMLElement>('schmancy-theme', anchorElement)
 */
export function discoverComponent<T extends HTMLElement>(
	componentTag: string,
	origin: Element | null,
): Observable<T | null> {
	const tag = componentTag.toUpperCase()
	const found = origin ? climbToAncestor(origin, el => el.tagName === tag) : null
	return of(found as T | null)
}

/** Ancestor climb across multiple candidate tags — first match wins, nearest to `origin`. */
export function discoverAnyComponent<T extends HTMLElement>(
	origin: Element | null,
	...componentTags: string[]
): Observable<T | null> {
	if (componentTags.length === 0 || !origin) return of(null)
	const tags = new Set(componentTags.map(t => t.toUpperCase()))
	const found = climbToAncestor(origin, el => tags.has(el.tagName))
	return of(found as T | null)
}

/**
 * The first element matching `selector` anywhere under `root`, piercing
 * shadow DOM. `root` defaults to `document` — the whole page.
 *
 * @example discoverElement<HTMLElement>('#app-card')
 * @example discoverElement<HTMLElement>('schmancy-fancy-card')  // a bare tag name is a valid selector too
 */
export function discoverElement<T extends HTMLElement>(
	selector: string,
	root: ParentNode = document,
): Observable<T | null> {
	return of(walkShadowPiercing(root, selector) as T | null)
}

/** Every element matching `selector` anywhere under `root`, piercing shadow DOM. */
export function discoverAllElements<T extends HTMLElement>(
	selector: string,
	root: ParentNode = document,
): Observable<T[]> {
	return of(walkShadowPiercingAll(root, selector) as T[])
}

/**
 * `discoverElement` under a name that reads naturally at either a CSS
 * selector or a plain component tag call site — `querySelector` already
 * treats a bare tag name as a valid selector, so there is nothing left to
 * dispatch on.
 */
export function discover<T extends HTMLElement>(query: string, root: ParentNode = document): Observable<T | null> {
	return discoverElement<T>(query, root)
}

/**
 * The nearest peer of `origin`'s own kind, found by climbing the composed
 * tree — the mechanism a component whose registration cascade must reach a
 * peer BESIDE its own content, not just above it, needs instead of
 * `@lit/context`'s own reach (ancestor/descendant only). Powers
 * `<schmancy-navigation-rail>`'s parent-rail discovery: a rail's normal shell
 * puts it beside its routed content — siblings under a grid, not
 * ancestor/descendant — so a plain context consume never sees across that
 * boundary.
 *
 * At each step up from `origin`, a hit is either the ancestor itself (the
 * wraps-its-content shape — `<rail><content><rail/></content></rail>`) or
 * found by searching, nearest-to-farthest, each of the child's own preceding
 * siblings' subtrees via `walkShadowPiercing` (the beside-its-content shape).
 * Only content that structurally PRECEDES `origin` — an earlier sibling, an
 * ancestor — ever counts: that is what keeps two siblings (a rail and the
 * content it sits beside, which itself contains a nested rail deeper in)
 * from each discovering the other as their own parent, since only the later
 * one's search would otherwise ever see the earlier.
 *
 * @example discoverAscendingPeer<HTMLElement>('schmancy-navigation-rail', this)
 */
export function discoverAscendingPeer<T extends Element>(selector: string, origin: Element): T | null {
	let child: Element = origin
	while (true) {
		const next = stepUp(child)
		if (!next) return null
		if (next.matches(selector)) return next as T
		if (child.parentElement === next) {
			let sibling = child.previousElementSibling
			while (sibling) {
				if (sibling.matches(selector)) return sibling as T
				const foundInSibling = walkShadowPiercing(sibling, selector)
				if (foundInSibling) return foundInSibling as T
				sibling = sibling.previousElementSibling
			}
		}
		child = next
	}
}
